Vendd.Scripts.load(function () {
    "use strict";
    
    /**
     * @typedef {Object} Language
     * @property {Number} Id
     * @property {String} TwoLetterCode
     * @property {String} ThreeLetterCode
     */

    /**
     * @typedef {Object} UiActionPath
     * @property {Number} ItemId
     * @property {String} Name
     * @property {String} MenuName
     */

    /**
     * @typedef {Object} UiActionLocalization
     * @property {Number} Id
     * @property {Nubmer} LanguageId
     * @property {String} Text
     */

    /**
     * @typedef {Object} UiAction
     * @property {Number} Id
     * @property {Number} ItemId
     * @property {Number} ItemPath
     * @property {Number} LinkItemId
     * @property {Number} LinkItemPathChecksum
     * @property {String} LinkItemEntityCode
     * @property {Boolean} Primary
     * @property {String} Parameters
     * @property {String} Anchor
     * @property {Number} Target
     * @property {Boolean} UseTargetItemName
     * 
     * @property {String} Breadcrumb
     * @property {String} Code
     * @property {Array<UiActionPath>} Paths
     * @property {Array<UiActionLocalization>} Text
     */

    /**
     * @typedef {UiAction} UiActionModel
     * @property {Boolean} Primary
     * @property {Boolean} IsActive
     * @property {Boolean} HasLinkItem
     * @property {Boolean} TargetSelfIsSelected
     * @property {Boolean} UseTargetItemName
     * @property {Boolean} IsJSLinkedItem
     * @property {Boolean} IsURLLinkedItem
     * @property {Boolean} IsTargetLinkedItem
     * @property {Boolean} Breadcrumb
     * @property {Boolean} LinkItemName
     */

    /**
     * @typedef {Object} UiActionFactoryContext
     * @property {Array<Language>} Languages
     * @property {Number} CurrentLanguageId
     */

    /**
     * @typedef {Object} UiActionFactory
     * @property {(obj: UiAction, config: UiActionFactoryContext) => UiActionModel} create
     */

    /**
     * @return {UiActionFactory}}
     */
    var uiActionFactory = function() {

        /**
         * 
         * @param {UiAction} obj 
         * @param {UiActionFactoryContext} context 
         */
        var addDefaultProperties = function(obj, context) {

            Object.defineProperty(obj, "IsActive", {
                get() {
                    return this._active !== false;
                },
                set(x) {
                    this._active = x;
                },
                enumerable: true
            });
            
            Object.defineProperty(obj, "HasLinkItem", {
                get() {
                    return this.LinkItemId > 0;
                },
                enumerable: true
            });
            
            Object.defineProperty(obj, "TargetSelfIsSelected", {
                get() {
                    return this.Target === 0;
                },
                enumerable: true
            });
            
            Object.defineProperty(obj, "IsJSLinkedItem", {
                get() {
                    return this.LinkedItemEntityCode === "JavascriptAction";
                },
                enumerable: true
            });

            Object.defineProperty(obj, "IsURLLinkedItem", {
                get() {
                    return this.LinkedItemEntityCode === "URL";
                },
                enumerable: true
            });

            Object.defineProperty(obj, "IsTargetLinkedItem", {
                get() {
                    return !this.IsJSLinkedItem && !this.IsURLLinkedItem;
                },
                enumerable: true
            });

            Object.defineProperty(obj, "Breadcrumb", {
                get() {
                    return _.map((obj.Paths || []), function (x) { return x.MenuName || x.Name; }).join(' > ');
                },
                enumerable: true
            });

            Object.defineProperty(obj, "LinkItemName", {
                get() {
                    
                    if((this.Paths ||[]).length <= 0) {
                        return '';
                    }

                    var item = _.last(this.Paths)

                    return item.MenuName || item.Name;
                },
                enumerable: true
            });
            
            Object.defineProperty(obj, "SelectedText", {
                get() {
                    return _.find(this.Text, function(x) { return x.LanguageId === context.CurrentLanguageId; });
                },
                // enumerable: true
            });
            
        };

        /**
         * 
         * @param {UiAction} obj 
         * @param {UiActionFactoryContext} context 
         */
        var addDefaultLocalizations = function(obj, context) {
                
            _.each(context.Languages, function(x) {

                var localization = _.find(obj.Text || [], function (loc) { return loc.LanguageId === x.Id; });;

                if(!!localization) {

                    return;

                }

                obj.Text.push({
                    Id: 0,
                    LanguageId: x.Id,
                    Text: ''
                });

            });
            
        }

        return {
            create: function(obj, context) {

                if(typeof _ !== 'function') {

                    throw new error(`underscore is undefined`);

                }

                // deep clone object
                var result = _.isFunction(_.merge)
                    ? _.merge({ __original: obj }, obj) // use lodash
                    : _.extend({}, { __original: obj }, JSON.parse(JSON.stringify(obj))) // use underscore.
                    ;

                addDefaultProperties(result, context);
                addDefaultLocalizations(result, context);

                return result;
            }
        };

    };
    
    /**
     * @typedef {Object} UiActionModelBuilder
     * @property {(obj: UiActionModel) => UiActionModel} toModelData
     */

    /**
     * @returns {UiActionModelBuilder}
     */
    var uiActionModelBuilder = function() {

        return {

            toModelData: function(obj) {

                if(typeof _ !== 'function') {

                    throw new error(`underscore is undefined`);

                }

                // deep clone object
                var action = _.isFunction(_.merge)
                    ? _.merge({}, obj) // use lodash
                    : _.extend({}, JSON.parse(JSON.stringify(obj))) // use underscore.
                    ;

                if(!action) {

                    return;

                }
                
                // delete private Properties
                Object.keys(action).forEach(function(property) { 

                    if(property[0] === '_') {
                        
                        delete action[property];
                    }

                });

                return action;

            }

        };

    };
        
    var factory = uiActionFactory();
    var modelBuilder = uiActionModelBuilder();

    var createUiActionModel = function(data) {

        /** @type {Array<{Code: String, Action: {UiAction}}>} */
        var data = _.map(Object.keys(data.Actions), function(x) { return { Code: x, Action: data.Actions[x] }; }) || [];
        /** @type {Array<UiActionModel>} */
        var actions = _.map(data, function(x) {

            /** @type {UiAction} */
            var defaultActionModel = {
                "Id": 0,
                "ItemId": 0,
                "LinkItemPathChecksum": null,
                "LinkItemId": 0,
                "Primary": false,
                "Parameters": "",
                "Anchor": "",
                "Target": 0,
                "_active": true,
                "Code": '',
                "Text": []
            };

            /** @type {UiAction} */
            var result = _.extend(
                defaultActionModel
                , x.Action
                , { "Code": x.Code } //ensures code is correct
                );

            /** @type {UiActionModel} */
            result = factory.create(result, {
                CurrentLanguageId: Vendd.inlineEdit.languageManager.current.Id,
                Languages: _.map(Vendd.inlineEdit.languageManager.all, function(x) { return { Id: x.Id, TwoLetterCode: x.TwoLetterCode,ThreeLetterCode: x.ThreeLetterCode }; })
            });

            return result;
        });

        var result = {};
        actions.forEach(function(x) { result[x.Code] = x });

        return result;

    };










    var Vendd = window.Vendd || {};
    
    var itemForm = function (element, definition, options) {

        var self = this;

        Vendd.inlineEdit.formControlBase.apply(self, [element, definition]);

        self.itemId = options.itemId;
        self.path = options.path;
        self.skuId = options.skuId;
        //self.itemId = element.data('parent-id');
        //self.path = element.data('path');
        //self.skuId = element.data('selected-id');

        self.entity = options.entity;
        self.languageId = options.languageId;
        self.siteId = options.siteId;
    };
    itemForm.prototype = Object.create(Vendd.inlineEdit.formControlBase.prototype);
    itemForm.prototype.name = 'itemForm';

    itemForm.prototype.init = function () {

        var self = this;

        return Vendd.Promises.GetOrDefault(function() { return Vendd.inlineEdit.formControlBase.prototype.init.apply(self); })
            .then(function()
            {
                return Vendd.api.call(Vendd.api.path('admin', '/api/InlineAdmin/GetItemPermissions'), {
                    EntityCode: "",
                    ItemId: self.itemId,
                    ItemPathChecksum: self.path,
                    LanguageCode: Vendd.inlineEdit.languageManager.all.find(function (x) { return x.Id === self.languageId }).TwoLetterCode
                    //RootItemCode: "vcfg_glb_servicesprospects"
                })
            })
            .then(function (response)
            {
                if (!response.HasPermission)
                {
                    throw new Error('unauthorized request');
                }

                var promises = [];

                promises.push(Vendd.api.call(Vendd.api.path('admin', '/api/InlineAdmin/GetFullItemAsync'), {
                    SiteId: self.siteId,
                    ItemId: self.itemId,
                    EntityCode: self.entity,
                    ItemPathChecksum: self.path
                }));

                var _codes = 'default';

                promises.push(Vendd.api.call(Vendd.api.path('admin', '/api/InlineAdmin/GetRootTreeModesAsync'), { codes: _codes })
                    .then(function (response) {

                        var treeMode = _.find(response.Items, function (x) {
                            return x.Code === _codes;
                        });

                        return Vendd.api.call(Vendd.api.path('admin', 'api/InlineAdmin/GetItemActions'), {
                            ItemId: self.itemId,
                            TreeLookupRootItemCode: treeMode.PageRootCode,
                            TreeLookupRootJSItemCode: treeMode.ActionRootCode,
                            TreeLookupRootURLItemCode: treeMode.URLRootCode,
                            LanguageCode: Vendd.inlineEdit.languageManager.all.find(function (x) { return x.Id === self.languageId }).TwoLetterCode
                        });

                    }));

                promises.push(Vendd.api.call(Vendd.api.path('admin', '/api/editor/GetEditorParameters'), {
                    SiteId: self.siteId,
                    ItemId: self.itemId,
                    EntityCode: self.entity,
                    ItemPathChecksum: self.path
                }));

                return Q.all(promises)
                    .then(function (response)
                    {
                        self.datasources = {
                            'Item': new Vendd.inlineEdit.Datasource(response[0]),
                            'uiActions': new Vendd.inlineEdit.Datasource(createUiActionModel(response[1])),
                            'parameters': new Vendd.inlineEdit.Datasource(response[2])
                        };
                    });

            });

    };

    itemForm.prototype.bind = function () {

        var self = this;

        return Vendd.Promises.GetOrDefault(function() { return Vendd.inlineEdit.formControlBase.prototype.bind.apply(self); })
            .then(function() { Vendd.MessageBus.publishAsync(self, new Vendd.inlineEdit.messages.datasourceChanged(self.definition.datasource.id)); });

    };

    itemForm.prototype.hasChanges = function () {

        var self = this;

        return !!_.find(Object.keys(self.datasources), function(x) 
        {
            var ds = self.datasources[x];

            return typeof ds.hasChanges === 'function' && ds.hasChanges();

        });

    };

    itemForm.prototype.discard = function() {

        var self = this;

        _.each(Object.keys(self.datasources), function(x)
        {
            var ds = self.datasources[x];

            if(!ds.hasChanges())
            {
                return;
            }

            ds.discard();

            Vendd.MessageBus.publishAsync(self, new Vendd.inlineEdit.messages.datasourceChanged(self.definition.datasource.id));

        });

        return;

    };

    itemForm.prototype.save = function() {

        var self = this;

        Vendd.inlineEdit.uiLoader.show();
        
        var model = self.datasources[self.definition.datasource.id].AsModel();
        var uiActionsModel = self.datasources['uiActions'].AsModel();
        
        var saveObject = JSON.parse(JSON.stringify(model));

        saveObject.ItemAttributes = _.map(model.ItemAttributes, function (x) {

            var result = {
                ItemAttributeId: x.ItemAttributeId,
                AttributeId: x.AttributeId,
                AttributeCode: x.AttributeCode,
                LanguageId: x.LanguageId ? x.LanguageId : null
            };

            if (typeof (x.ValueDatetime) != 'undefined' && x.ValueDatetime != null) {
                result.ValueDatetime = x.ValueDatetime;
            }

            if (typeof (x.ValueBool) != 'undefined' && x.ValueBool != null) {
                result.ValueBool = x.ValueBool;
            }

            if (typeof (x.ValueSmallText) != 'undefined' && x.ValueSmallText != null) {
                result.ValueSmallText = x.ValueSmallText;
            }

            if (typeof (x.ValueDecimal) != 'undefined' && x.ValueDecimal != null) {
                result.ValueDecimal = x.ValueDecimal;
            }

            if (typeof (x.ValueInt) != 'undefined' && x.ValueInt != null) {
                result.ValueInt = x.ValueInt;
            }

            if (typeof (x.ValueBigText) != 'undefined' && x.ValueBigText != null) {
                result.ValueBigText = x.ValueBigText;
            }

            if (typeof (x.ValueCodeText) != 'undefined' && x.ValueCodeText != null) {
                result.ValueCodeText = x.ValueCodeText;
            }

            return result;
        });

        saveObject.ItemsMeta = _.map(model.ItemsMeta, function (x) {

            return {
                "LanguageId": x.LanguageId,
                "HTMLMetaTypeId": x.HTMLMetaTypeId,
                "ContentValue": x.ContentValue ? x.ContentValue : ""
            };

        });

        saveObject.KeyValues = _.map(_.filter(model.ItemAttributes, function (x) { return x.AttributeCode === 'ItemsKeyValues_KeyValueContainer' }), function (x) {

            return {
                LanguageId: x.LanguageId,
                Text: x.ValueCodeText,
            };
        });

        saveObject.UIActions = modelBuilder.toModelData(uiActionsModel);

        return Vendd.api.call(Vendd.api.path('admin', 'api/InlineAdmin/UpdateItemAsync'), saveObject)
            .then(function (result) {

                Vendd.inlineEdit.uiLoader.hide();

                return result;
            });

    };

    Vendd.inlineEdit.itemForm = itemForm;

});